import { notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import type { TemplateVersionInfo } from '../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { stringToDateTimeNullSafe } from '../helpers'

const filterVersionInfosByDateAndChangeset = ({
  versionInfos,
  date,
}: {
  versionInfos: TemplateVersionInfo[]
  date: Date
}): Optional<TemplateVersionInfo> => {
  const comparableDate = date.getTime()

  return versionInfos.findLast(
    (v) =>
      stringToDateTimeNullSafe(v.fromDate, Number.NEGATIVE_INFINITY) <=
      comparableDate,
  )
}

export const useTemplateVersionInfo = ({
  id,
  date,
}: {
  id: Optional<string>
  date: Date
}): Optional<TemplateVersionInfo> => {
  const templateVersionInfos = useTemplateVersionInfos(id)

  return useMemo(
    () =>
      id && templateVersionInfos
        ? filterVersionInfosByDateAndChangeset({
            versionInfos: templateVersionInfos,
            date,
          })
        : undefined,
    [id, date, templateVersionInfos],
  )
}

export const useAllTemplateVersionInfosForDate = ({
  id,
  date,
}: {
  id: Optional<string>
  date: Date
}) => {
  const templateVersionInfos = useTemplateVersionInfos(id)
  const activeVersionInfo = useTemplateVersionInfo({ id, date })

  return useMemo(
    () =>
      templateVersionInfos?.filter(
        (versionInfo) => versionInfo.fromDate === activeVersionInfo?.fromDate,
      ),
    [templateVersionInfos, activeVersionInfo?.fromDate],
  )
}

export const useTemplateVersionInfos = (
  id: Optional<string>,
): TemplateVersionInfo[] => {
  const { applicationTemplateVersionInfos: templateVersionInfos } =
    useApplicationViewOperationsContext()

  return useMemo(
    () =>
      id && templateVersionInfos
        ? (templateVersionInfos.get(id)?.filter(notEmpty) ?? [])
        : [],
    [id, templateVersionInfos],
  )
}

export const useAllTemplateVersionInfos = (
  versionIds: string[],
): TemplateVersionInfo[] | undefined => {
  const { applicationTemplateVersionInfos: templateVersionInfos } =
    useApplicationViewOperationsContext()

  return useMemo(
    () =>
      versionIds && templateVersionInfos
        ? versionIds
            .flatMap((id) => templateVersionInfos.get(id))
            .filter(notEmpty)
        : undefined,
    [versionIds, templateVersionInfos],
  )
}

const firstActiveVersion = (
  versions: TemplateVersionInfo[],
  at: string,
): Optional<TemplateVersionInfo> => {
  const atDate = stringToDateTimeNullSafe(at, Number.POSITIVE_INFINITY)

  return versions.findLast(
    (version) =>
      stringToDateTimeNullSafe(version.fromDate, Number.NEGATIVE_INFINITY) <=
      atDate,
  )
}

export const useVersionInfosAt = (
  at: string,
): Map<string, Optional<TemplateVersionInfo>> | undefined => {
  const { applicationTemplateVersionInfos: templateVersionInfos } =
    useApplicationViewOperationsContext()

  return useMemo(
    () =>
      templateVersionInfos
        ? new Map(
            Array.from(templateVersionInfos, ([key, value]) => [
              key,
              firstActiveVersion(value, at),
            ]),
          )
        : undefined,
    [at, templateVersionInfos],
  )
}
